import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Announcement } from "../../Core/Content/Types";
import { announcementSettings } from "../../Core/Content/announcement.mjs";
import "./Styles.scss";

export function NoticePiece({ settings }: { settings: Announcement }) {
  const [paused, setPaused] = useState(false);
  if (!settings.enabled || !settings.message.trim()) return null;
  return (
    <aside className="sow-notice" aria-label="Ordering notice">
      <strong className="sow-notice-label">ORDERING NOTICE</strong>
      <span className="sow-notice-sr">{settings.message}</span>
      <div
        className={`sow-notice-window ${paused ? "is-paused" : ""}`}
        aria-hidden="true"
      >
        <div
          className="sow-notice-track"
          style={{
            animationDuration: `${Math.max(22, settings.message.length / 4)}s`,
          }}
        >
          {[0, 1].map((copy) => (
            <span className="sow-notice-message" key={copy}>
              {settings.message}
              <span className="sow-notice-dot">✦</span>
            </span>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="sow-notice-pause"
        aria-label={
          paused ? "Resume notice scrolling" : "Pause notice scrolling"
        }
        aria-pressed={paused}
        onClick={() => setPaused(!paused)}
      >
        {paused ? "Play" : "Pause"}
      </button>
    </aside>
  );
}

export default function SiteNoticeLayout({
  children,
  preview = false,
  settings,
}: {
  children: React.ReactNode;
  preview?: boolean;
  settings?: Announcement;
}) {
  const layout = useRef<HTMLDivElement>(null);
  const [noticeHeight, setNoticeHeight] = useState(54);
  const [published, setPublished] = useState<Announcement>();
  useEffect(() => {
    if (preview) return;
    const controller = new AbortController();
    fetch("/api/cms/pages/navigation/published", {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.ok && !controller.signal.aborted)
          setPublished(announcementSettings(data.value?.content));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [preview]);
  const notice = preview
    ? settings || announcementSettings({})
    : published || announcementSettings({});
  useLayoutEffect(() => {
    const element = layout.current?.querySelector(":scope > .sow-notice");
    if (!element) {
      setNoticeHeight(0);
      return;
    }
    const update = () =>
      setNoticeHeight(element.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [notice.enabled, notice.message]);
  return (
    <div
      ref={layout}
      className="sow-site-layout"
      style={
        {
          "--sow-notice-height":
            notice.enabled && notice.message.trim()
              ? `${noticeHeight}px`
              : "0px",
        } as React.CSSProperties
      }
    >
      <NoticePiece settings={notice} />
      {children}
    </div>
  );
}
